import { Card, CardContent, Popover, Stack, Typography } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";
import ContextMenu, { ContextMenuItem } from "./ContextMenu";
import { ErrorGuard } from "./ErrorGuard";
import { LoadingGuard } from "./LoadingGuard";

export interface ValueDisplayCardProps {
  title: string;
  value: number | string;
  inf?: string | React.ReactNode;
  grow?: boolean;
  popover?: React.ReactElement;
  contextMenuItems?: ContextMenuItem[];
  isLoading?: boolean;
  error?: Error | string | null;
  onDoubleClick?: () => void;
}

export function ValueDisplayCard({
  title,
  value,
  inf,
  grow = true,
  isLoading,
  error,
  popover,
  contextMenuItems,
  onDoubleClick,
}: ValueDisplayCardProps) {
  const [popoverAnchorEl, setPopoverAnchorEl] = useState<HTMLDivElement | null>(
    null
  );
  const cardRef = useRef<HTMLDivElement>(null);
  const popoverOpen = Boolean(popoverAnchorEl);
  const popoverId = popoverOpen ? "simple-popover" : undefined;

  const handleClick = useMemo(
    () => () => {
      if (cardRef.current) {
        setPopoverAnchorEl(cardRef.current);
      }
    },
    []
  );

  useEffect(() => {
    cardRef.current?.addEventListener("click", handleClick);
  }, [cardRef.current]);

  let card = (
    <CardContent ref={cardRef}>
      <Stack>
        <Typography variant="caption">{title}</Typography>
      </Stack>
      <LoadingGuard isLoading={isLoading}>
        <ErrorGuard error={error}>
          <Typography variant="h6">
            {value}
            {inf && <Typography variant="caption">({inf})</Typography>}
          </Typography>
        </ErrorGuard>
      </LoadingGuard>
    </CardContent>
  );

  if (contextMenuItems) {
    card = <ContextMenu items={contextMenuItems}>{card}</ContextMenu>;
  }

  return (
    <>
      <Card style={{ flex: grow ? 1 : "unset" }} onDoubleClick={onDoubleClick}>
        {card}
      </Card>
      {popover && (
        <Popover
          id={popoverId}
          open={popoverOpen}
          anchorEl={popoverAnchorEl}
          anchorOrigin={{ vertical: "center", horizontal: "center" }}
          onClose={() => {
            setPopoverAnchorEl(null);
          }}
        >
          {popover}
        </Popover>
      )}
    </>
  );
}
